import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Play } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { industryAds } from '@/config/ads';
import AdSection from '@/components/AdSection';
import { useCMSContent } from '@/hooks/use-cms-content';
import { fetchSolutionPageBySlug, type SolutionPageData } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

interface IndustryPageProps {
  slug?: string;
  industry: string;
  heroHeadline: string;
  heroSubheadline: string;
  section2Title: string;
  section2Content: string;
  section3Title: string;
  section3Content: string;
  section4Title: string;
  section4Content: string;
  heroImage: string;
  heroAd?: string;
  section2Ad?: string;
  section3Ad?: string;
  accentColor: string;
  stats: {
    value: string;
    label: string;
  }[];
  benefits: string[];
}

const IndustryPageTemplate: React.FC<IndustryPageProps> = (props) => {
  const { slug, ...hardcodedProps } = props;

  const fetchFn = useCallback(
    () => fetchSolutionPageBySlug(slug || ''),
    [slug],
  );
  const { data: cmsData } = useCMSContent<SolutionPageData | null>(
    `solution-${slug}`,
    fetchFn,
    null,
  );

  const {
    industry,
    heroHeadline,
    heroSubheadline,
    section2Title,
    section2Content,
    section3Title,
    section3Content,
    section4Title,
    section4Content,
    heroImage,
    heroAd,
    section2Ad,
    section3Ad,
    accentColor,
    stats,
    benefits,
  } = cmsData ? { ...hardcodedProps, ...cmsData } : hardcodedProps;
  const navigate = useNavigate();

  // Extract gradient colors from accentColor for consistency
  const getGradientFromAccent = () => {
    if (accentColor.includes('blue')) return 'from-blue-500 to-cyan-500';
    if (accentColor.includes('pink')) return 'from-pink-500 to-rose-500';
    if (accentColor.includes('green') || accentColor.includes('emerald')) return 'from-emerald-500 to-teal-500';
    if (accentColor.includes('purple') || accentColor.includes('violet')) return 'from-violet-500 to-purple-500';
    if (accentColor.includes('amber') || accentColor.includes('orange')) return 'from-amber-500 to-orange-500';
    if (accentColor.includes('red')) return 'from-red-500 to-rose-500';
    if (accentColor.includes('indigo')) return 'from-indigo-500 to-blue-500';
    return 'from-[#635bff] to-[#00d4ff]';
  };
  const gradient = getGradientFromAccent();

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Solutions', item: '/solutions' },
        { name: industry },
      ]} />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 lg:pt-0 overflow-hidden">
        {/* Stripe-style gradient background */}
        <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
          {/* Grid pattern */}
          <div className="absolute inset-0 grid-pattern" />
          
          {/* Animated gradient orbs */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] opacity-30 dark:opacity-40">
            <div className={`absolute top-0 right-0 w-full h-full bg-gradient-to-br ${gradient} rounded-full blur-3xl animate-float`} />
          </div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] opacity-20 dark:opacity-30">
            <div className={`absolute bottom-0 left-0 w-full h-full bg-gradient-to-tr ${gradient} rounded-full blur-3xl animate-float-delayed`} />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="max-w-xl">
              <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${gradient} bg-opacity-10 border border-white/20 text-sm font-medium text-white mb-8`}>
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />
                {industry}
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                <span className="text-slate-900 dark:text-white">{heroHeadline}</span>
              </h1>

              
              <p className="mt-8 text-lg lg:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
                {heroSubheadline}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button onClick={() => navigate('/demo')} className="stripe-button inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white rounded-full group">
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => navigate('/gallery')} className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-slate-700 dark:text-slate-200 bg-white/80 dark:bg-white/10 backdrop-blur-sm border border-slate-200 dark:border-white/20 rounded-full hover:bg-white dark:hover:bg-white/20 hover:border-slate-300 dark:hover:border-white/30 transition-all group">
                  <Play className="mr-2 h-5 w-5" />
                  View Examples
                </button>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative lg:pl-8">
              {/* Glow effect behind card */}
              <div className={`absolute -inset-4 bg-gradient-to-r ${gradient} rounded-3xl blur-2xl opacity-30 dark:opacity-20`} />
              
              {/* Main Image/Ad Display */}
              <div className="relative">
                <div className="relative bg-white dark:bg-slate-800/50 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 overflow-hidden border border-slate-200/50 dark:border-white/10 backdrop-blur-xl">
                  {heroAd ? (
                    <div className="aspect-[4/3] relative">
                      <AdSection adUrl={heroAd} sectionId={`${industry}-hero`} className="absolute inset-0" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] relative">
                      <img src={heroImage} alt={industry} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                      <div className="absolute bottom-6 left-6 right-6">
                        <span className={`inline-block px-3 py-1 text-xs font-semibold text-white bg-gradient-to-r ${gradient} rounded-full`}>
                          {industry}
                        </span>
                        <p className="mt-3 text-white font-semibold text-lg">Interactive Ad Solutions</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Interactive Indicator — temporarily hidden
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full shadow-lg border border-white/50 dark:border-white/10">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Interactive</span>
                  </div>
                  */}
                </div>
              </div>

              {/* Floating Stats Cards — temporarily hidden
              <div className="absolute -left-8 top-1/4 glass-card rounded-xl shadow-xl p-4 hidden lg:block animate-float">
                <div className={`text-2xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>{stats[0]?.value}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{stats[0]?.label}</div>
              </div>
              <div className="absolute -right-8 bottom-1/4 glass-card rounded-xl shadow-xl p-4 hidden lg:block animate-float-delayed">
                <div className="text-2xl font-bold text-emerald-500">{stats[1]?.value}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{stats[1]?.label}</div>
              </div>
              */}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900 dark:bg-slate-950">
          <div className="absolute inset-0 grid-pattern opacity-30" />
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] opacity-20">
            <div className={`w-full h-full bg-gradient-to-br ${gradient} rounded-full blur-3xl`} />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-slate-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Section 2 */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
          <div className="absolute inset-0 grid-pattern" />
          <div className="absolute right-0 top-0 w-[600px] h-[600px] opacity-20 dark:opacity-30">
            <div className={`w-full h-full bg-gradient-to-br ${gradient} rounded-full blur-3xl`} />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
                {section2Title}
              </h2>
              <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                {section2Content}
              </p>
              <ul className="mt-8 space-y-4">
                {benefits.slice(0, 3).map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-lg mt-0.5`}>
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-700 dark:text-slate-300 font-medium">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className={`absolute -inset-4 bg-gradient-to-r ${gradient} rounded-3xl blur-2xl opacity-20`} />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10 dark:shadow-black/30 border border-slate-200/50 dark:border-white/10">
                {section2Ad ? (
                  <div className="aspect-[16/9] relative">
                    <AdSection adUrl={section2Ad} sectionId={`${industry}-section2`} className="absolute inset-0" lazy />
                  </div>
                ) : (
                  <React.Fragment>
                    <img src="https://media.adventive.com/site/application-images/adbuilder-screenshot.png" alt={section2Title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                  </React.Fragment>
                )}
                {/* Live Ad Preview badge — temporarily hidden
                <div className="absolute top-4 right-4 glass-card px-3 py-1.5 rounded-full">
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Live Ad Preview</span>
                </div>
                */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-50/50 dark:bg-white/[0.02]">
          <div className="absolute inset-0 grid-pattern" />
          <div className="absolute left-0 bottom-0 w-[600px] h-[600px] opacity-20 dark:opacity-30">
            <div className={`w-full h-full bg-gradient-to-tr ${gradient} rounded-full blur-3xl`} />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="lg:order-2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
                {section3Title}
              </h2>
              <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                {section3Content}
              </p>
              {/* <div className="mt-10 flex gap-10">
                <div>
                  <div className={`text-4xl font-extrabold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                    3x
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">Higher CTR</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold text-emerald-500">
                    +47%
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">Engagement</div>
                </div>
              </div> */}
            </div>
            <div className="lg:order-1 relative">
              <div className={`absolute -inset-4 bg-gradient-to-r ${gradient} rounded-3xl blur-2xl opacity-20`} />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10 dark:shadow-black/30 border border-slate-200/50 dark:border-white/10">
                {section3Ad ? (
                  <div className="aspect-[16/9] relative">
                    <AdSection adUrl={section3Ad} sectionId={`${industry}-section3`} className="absolute inset-0" lazy />
                  </div>
                ) : (
                  <React.Fragment>
                    <img src="https://media.adventive.com/site/application-images/analytics-screenshot.png" alt={section3Title} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Ideal For */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-white dark:bg-[#0a0a0f]">
          <div className="absolute inset-0 grid-pattern" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">{section4Title}</h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              {section4Content}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="group relative bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-slate-200/50 dark:border-white/10 hover:bg-white dark:hover:bg-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all duration-300 text-center stripe-card">
                <div className={`w-14 h-14 mx-auto rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold text-xl mb-4 shadow-lg`}>
                  {index + 1}
                </div>
                <p className="text-slate-700 dark:text-slate-300 font-medium">{benefit}</p>
                
                {/* Hover glow effect */}
                <div className={`absolute -inset-px bg-gradient-to-r ${gradient} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10 blur-xl`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Stripe-style gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-[#00d4ff]" />
          
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 opacity-50">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent" />
          </div>
          
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '60px 60px'
            }} />
          </div>
          
          {/* Floating orbs */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-white/20 to-transparent rounded-full blur-3xl animate-float" />
          </div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-20">
            <div className="w-full h-full bg-gradient-to-tr from-[#00d4ff]/50 to-transparent rounded-full blur-3xl animate-float-delayed" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight">
              Ready to transform your {industry.toLowerCase()} ads?
            </h2>
            <p className="mt-6 text-lg lg:text-xl text-white/80 leading-relaxed">
              See how Adventive can help you create interactive ads that drive real results.
            </p>
            
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <button onClick={() => navigate('/demo')} className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-[#635bff] bg-white rounded-full hover:bg-slate-100 transition-all hover:shadow-xl hover:shadow-black/20 group">
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate('/gallery')} className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/20 hover:border-white/30 transition-all group">
                <Play className="mr-2 h-5 w-5" />
                View Ad Gallery
              </button>
            </div>

            {/* Stats */}
            {/* <div className="mt-16 grid grid-cols-3 gap-8 pt-12 border-t border-white/20">
              <div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">3x</div>
                <div className="mt-2 text-sm text-white/60 font-medium">Higher engagement</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">50%</div>
                <div className="mt-2 text-sm text-white/60 font-medium">Faster production</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">40+</div>
                <div className="mt-2 text-sm text-white/60 font-medium">Ad server integrations</div>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustryPageTemplate;
